/* eslint-disable max-lines */
import { Injectable, NgZone } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { get } from 'scriptjs';
import { SetNotifications } from '../../store/actions/notifications.actions';
import { MessengerNotificationsAdapter } from '../../data/notifications/messenger-notifications.adapter';
import { MessagesService } from './messages.service';
import { MessengerApiService } from './messenger-api.service';
import { Router } from '@angular/router';
import { SnackBarService } from '../snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { SUPPORT_TOPICS } from '@shared/constants/support-topics';
import { SupportFaqDialogComponent } from '@shared/components/dialogs/support-faq-dialog/support-faq-dialog.component';
import { UserService } from '../user.service';
import { TranslocoService } from '@jsverse/transloco';
import { setDisabled } from '../../store/actions/messenger.actions';
import { USER_ROLE } from '../../store/reducers/session-info.reducer';
import { consentUrlsFeature } from '@core/store/reducers/consent-urls.reducer';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { MessengerConsentLinks } from './messenger-consent-links.model';
import { DialogData, XtDialogComponent } from '@xt/ui-kit';
import { Patient } from '@core/data/persons/patient.model';
import { PersonWithMatrixId } from '@core/data/persons/messaging-person.model';

import {
  selectAuditPollInterval,
  selectBffBaseUrl,
  selectIsShowMessenger,
  selectMessaging,
} from '../../store/selectors/api-config.selectors';

import {
  selectKeepAliveClientId,
  selectKeepAliveClientSecret,
} from '../../store/selectors/app-config.selectors';

import {
  userRoleNewSelector,
  userRoleSelector,
} from '../../store/selectors/user-role.selectors';
import { AuditEvent } from '@core/data/notifications/notifications.model';
import { PatientRepresentation } from '@xt/domain-model';

@Injectable({
  providedIn: 'root',
})
export class MessengerService {
  secureMessaging: any;
  isLoginFailed: boolean = false;
  isStarted = false;
  config: string;
  objTest: any;
  loginToken: null;
  userSubId: any;
  matrixId = null;
  authorizationCode = null;
  databaseSecret: null;
  isNativeClient = false;
  chatExportArray: string | any[];
  // Indicates how often the messenger has tried to start
  startCounter: number = 0;

  private readonly maxStarts = 3;

  auditPollInterval = 60000;

  private readonly consents$: Observable<Record<string, any>> =
    this.store.select(consentUrlsFeature.selectConsents);

  private readonly userRole$: Observable<USER_ROLE> = this.store.pipe(
    select(userRoleSelector),
  );

  private readonly keepAliveClientId$: Observable<string> = this.store.pipe(
    selectKeepAliveClientId,
  );

  private readonly selectKeepAliveClientSecret$: Observable<string> =
    this.store.pipe(selectKeepAliveClientSecret);

  constructor(
    public messagesService: MessagesService,
    private messengerApiService: MessengerApiService,
    private messengerNotificationsAdapter: MessengerNotificationsAdapter,
    private store: Store,
    private userService: UserService,
    private router: Router,
    private sb: SnackBarService,
    public dialog: MatDialog,
    private translocoService: TranslocoService,
    public _zone: NgZone,
  ) {}

  generateConfig(apiConfig) {
    return combineLatest([
      this.consents$,
      this.userRole$,
      this.keepAliveClientId$,
      this.selectKeepAliveClientSecret$,
      this.getTranslationsByLanguage(this.translocoService.getActiveLang()),
    ]).pipe(
      take(1),
      map(
        ([
          consentUrls,
          userRole,
          clientId,
          clientSecret,
          { language, languageJson },
        ]) => {
          const config = apiConfig;

          this.store.pipe(selectBffBaseUrl).subscribe((bff) => {
            config['curamentaBackend'] = bff;
          });

          // we have to check all config string values and convert them to proper types
          Object.entries(apiConfig).forEach(([key, value]) => {
            if (value === 'true' || value === 'false') {
              config[key] = value === 'true';
            } else {
              config[key] = value;
            }
          });

          config['keepAliveEnabled'] = !!clientId;

          if (clientId) {
            config['keepAliveClientId'] = clientId;
          }

          if (clientId && typeof clientSecret === 'string') {
            config['keepAliveClientSecret'] = clientSecret;
          }

          config['language'] = language;
          config['languageJson'] = languageJson || {};

          config['loginConfig']['environment'] = this.isNativeClient
            ? 'app'
            : 'web';
          config['loginConfig']['authorizationCode'] =
            this.authorizationCode || null;
          config['loginConfig']['databaseSecret'] = this.databaseSecret || null;
          config['matrixId'] = this.matrixId;
          config['userId'] = this.userSubId;
          config['localeConfig'] = {
            localeCode: language,
            language: languageJson || {},
            urls: this.mapUrlsToConfig(consentUrls, userRole),
          };

          this.objTest = JSON.parse(JSON.stringify(config));

          config['loginConfig']['portalAccessToken'] = this.loginToken || null;

          this.config = JSON.stringify(config);

          return this.config;
        },
      ),
    );
  }

  isMaxRetryCountReached() {
    return this.startCounter >= this.maxStarts;
  }

  startMessenger() {
    if (!this.showMessenger() || this.isMaxRetryCountReached()) return;
    this.startCounter++;
    this.appendToConsoleString(
      'Start Messenger retry: ' + this.startCounter + '/' + this.maxStarts,
    );
    
    this.userService.resourceId$
      .pipe(
        mergeMap(() => this.store.pipe(select(userRoleNewSelector))),
        take(1),
      )
      .subscribe((role) => {
        if (
          role &&
          role.some((role) =>
            [
              USER_ROLE.HCP,
              USER_ROLE.PATIENT,
              USER_ROLE.VERIFIED_REPRESENTATIVE,
            ].includes(role),
          )
        ) {
          if (this.matrixId != null || this.isNativeClient) {
            this.startMessengerPreparation();
          } else {
            this.messengerApiService.getMatrixId$().subscribe((id) => {
              this.matrixId = id;
              this.startMessengerPreparation();
            });
          }
        }
      });
  }

  startMessengerPreparation() {
    combineLatest([
      this.store.pipe(selectMessaging),
      this.store.pipe(selectAuditPollInterval),
    ])
      .pipe(
        tap(([messaging, auditPollInterval]) => {
          this.auditPollInterval = auditPollInterval * 1000;
        }),
        switchMap(([messaging, auditPollInterval]) =>
          this.generateConfig(messaging),
        ),
        take(1),
      )
      .subscribe(() => {
        this.initMessenger();
      });
  }

  initMessenger() {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    const secureMessagingInitial = 'secure_messaging_loader.dart.0.0.1-SNAPSHOT.4540.js';
    get(secureMessagingInitial, () => {
      const scope = window as any;
      this.secureMessaging = scope.secureMessaging;
      const self = this;

      self.appendToConsoleString('Messenger was started');

      self.secureMessaging.onError(function (error: any) {
        self.appendToConsoleString(error);

        if (!self.isLoginFailed) {
          self.showErrorMessage(error.error);
        }
      });

      self.appendToConsoleString('Secure messaging client initialized with config: ' + JSON.stringify(self.config));

      self.secureMessaging.run(self.config, async function () {
        self.store.dispatch(setDisabled({ disabled: false }));
        self.appendToConsoleString('Secure messaging client initialized');

        self.secureMessaging.onAuthenticationRequired(function () {
          self.isLoginFailed = true;
          self.appendToConsoleString(
            'Authentication failed - please log in to use messaging!',
          );
          self.showErrorMessage('app.errors.403');
        });

        self.getNotifications();
        setInterval(() => self.getNotifications(), self.auditPollInterval);

        self.secureMessaging.onInfoClicked(function () {
          self.openSupportDialog(false);
        });

        self.secureMessaging.onProviderInfoClicked(function () {
          self.openSupportDialog(true);
        });

        self.secureMessaging.onDashboardRoomItemClick((roomUrl) => {
          self.navigateTo(roomUrl);
        });
      });

      self.setMessengerLocale();

      self.secureMessaging.onFileDownload((data) => {
        if (self.isNativeClient) {
          self.chatExportArray = data['base64'].match(/.{1,900}/g);
          const messengerPackage = {
            type: 'messengerChatExport',
            numberOfPackages: self.chatExportArray.length,
            title: data['name'],
          };
          window.alert(JSON.stringify(messengerPackage));
        }
      });

      self.secureMessaging.onMatrixAuthenticationRequired(function (url) {
        self.isStarted = false;

        const json = {
          type: 'matrixAuthentication',
          data: url,
        };

        if (self.isNativeClient) {
          if (self.authorizationCode == null) {
            window.alert(JSON.stringify(json));
          } else {
            self.startMessenger();
          }
        } else {
          self.appendToConsoleString(
            '[Messenger Service]: Not Native & Matrix Authentication failed - please log in again to use messaging!',
          );
          self.isLoginFailed = true;
          if (self.isMaxRetryCountReached()) {
            self.showErrorMessage('messenger.on-matrix-auth-required');
            return;
          }
          self.startMessenger();
        }
      });
    });
  }

  private setMessengerLocale(): void {
    combineLatest([
      this.consents$,
      this.userRole$,
      this.getTranslationsByLanguage(this.translocoService.getActiveLang()),
    ])
      .pipe(take(1))
      .subscribe(([consents, userRole, activeLang]) => {
        try {
          const localeData = {
            localeCode: activeLang.language,
            language: activeLang.languageJson,
            urls: this.mapUrlsToConfig(consents, userRole),
          };
          this.secureMessaging.changeLocale(JSON.stringify(localeData));
        } catch (er) {
          console.error('Error setting locale', er);
        }
      });
  }

  mapUrlsToConfig(consents, userRole: USER_ROLE): MessengerConsentLinks {
    return {
      termsOfUse:
        userRole === USER_ROLE.REPRESENTATIVE
          ? consents?.nutzung_vertreter
          : consents?.nutzung_3 || '',
      privacyPolicyProfessional: consents?.dse_prof || '',
      privacyPolicyPatient: consents?.dse_pat || '',
      privacyPolicyRepresentative: consents?.dse_vertreter || '',
    };
  }

  setMessengerToken(token) {
    if (this.secureMessaging) {
      this.secureMessaging.setAccessToken(token);
    }
  }

  setLoginToken(token) {
    this.loginToken = token;
    this.isNativeClient = true;
  }

  setMessengerCode(code, state, iss) {
    this.authorizationCode = {
      code: code,
      state: state,
      iss: iss,
    };
    this.startCounter = 0;
    this.isStarted = false;
    this.startMessenger();
  }

  setMatrixId(matrixId) {
    this.isStarted = false;
    this.matrixId = matrixId;
  }

  setUserSubId(id) {
    this.userSubId = id;
  }

  setDatabaseSecret(databaseSecret) {
    this.isStarted = false;
    this.databaseSecret = databaseSecret;
  }

  runApp() {
    if (this.secureMessaging) {
      this.secureMessaging.mount();
    }
  }

  getNotifications() {
    this.secureMessaging.notifications((notifications: any) => {
      this.setNotifications(notifications);
      this.messagesService.setUnreadMessages(
        this.stringifyArray(notifications),
      );
    });
  }

  openSupportDialog(isProvider) {
    this._zone.run(() => {
      this.dialog.open(SupportFaqDialogComponent, {
        width: '880px',
        maxHeight: '90dvh',
        data: {
          topic: isProvider
            ? SUPPORT_TOPICS.MESSAGESPROVIDER.code
            : SUPPORT_TOPICS.MESSAGES.code,
          type: 'info',
        },
      });
    });
  }

  setNotifications(notifications) {
    const transformedNotifications =
      this.messengerNotificationsAdapter.adaptHashedNotifications(
        notifications,
      );

    this.store.dispatch(
      SetNotifications({
        notifications: {
          list: transformedNotifications,
          type: 'messenger',
        },
      }),
    );
  }

  navigateTo(path) {
    this._zone.run(() => {
      this.router.navigateByUrl(`dashboard/messages#${path}`);
    });
  }

  showErrorMessage(message) {
    this._zone.run(() => {
      this.sb.error(message);
    });
  }

  markAsRead(notifications: AuditEvent[]) {
    const notificationsToMarkRead = notifications.map((notification) => {
      return {
        roomId: notification.room.roomId,
        eventId: notification.room.eventId,
      };
    });

    this.secureMessaging.setReadEvent(
      this.stringifyArray(notificationsToMarkRead),
      (unreadNotifications: any) => {
        this.appendToConsoleString(unreadNotifications);
        this.messagesService.setUnreadMessages(
          this.stringifyArray(unreadNotifications),
        );
      },
    );
  }

  logout(): Observable<any> {
    if (
      this.secureMessaging &&
      typeof this.secureMessaging.logout === 'function'
    ) {
      return new Observable((observer) => {
        this.secureMessaging.logout(() => {
          this.appendToConsoleString('Messenger Client logged out');
          observer.next(true);
          observer.complete();
        });
      });
    } else {
      return of(false);
    }
  }

  stringifyArray(array) {
    return JSON.stringify(array, null, 2);
  }

  appendToConsoleString(text: string, items = []) {
    console.log(text, items);
  }

  showMessenger() {
    let isShowMessenger;
    this.store
      .pipe(selectIsShowMessenger)
      .subscribe((value) => (isShowMessenger = value));

    return isShowMessenger;
  }

  public sendDirectMessage(messagingPerson: PersonWithMatrixId): void {
    if (!messagingPerson?.matrixId) {
      this.showMessagingDisabledDialog(messagingPerson.person);
    } else {
      this.messengerApiService
        .personHasMatrixFlag$(messagingPerson)
        .subscribe((hasMatrixFlag) => {
          if (hasMatrixFlag) {
            this.navigateToChatRoom(messagingPerson);
          } else {
            this.showMessagingDisabledDialog(messagingPerson.person);
          }
        });
    }
  }

  private showMessagingDisabledDialog(
    person: Patient | PatientRepresentation,
  ): void {
    this.dialog.open<XtDialogComponent, DialogData>(XtDialogComponent, {
      width: '640px',
      data: {
        title: 'app.labels.hint',
        content: this.translocoService.translate(
          'messenger.messaging-not-available',
          { personDisplayName: person.name },
        ),
        confirmButtonLabel: 'app.labels.close',
      },
    });
  }

  private navigateToChatRoom(messagingPerson: PersonWithMatrixId): void {
    try {
      this.secureMessaging.createChat(messagingPerson.matrixId, (roomUrl) => {
        this.navigateTo(roomUrl);
      });
    } catch (error) {
      console.error('Cannot create a messenger chat', error);
      this.showErrorMessage('app.errors.somethingWentWrong');
    }
  }

  private getTranslationsByLanguage(
    language: string,
  ): Observable<{ language: string; languageJson: unknown }> {
    const languageJson =
      this.translocoService.translateObject('messenger-client');

    return typeof languageJson === 'string' // Typeof string means the languages are not yet loaded
      ? this.translocoService.events$.pipe(
          filter((e) => e.type === 'translationLoadSuccess'), // Wait till the translations are loaded
          map((e) => {
            if (e.payload.langName === language) {
              return {
                language,
                languageJson:
                  this.translocoService.translateObject('messenger-client'),
              };
            } else {
              return {
                language,
                languageJson: {},
              };
            }
          }),
          take(1),
        )
      : of({ language, languageJson: languageJson });
  }
}
